import axios, { AxiosInstance } from 'axios';
import { EventConstants } from '../../constants/events';
import {
  DeclarationConstants,
  TOAST_AUTO_HIDE_DURATION,
} from '../../constants/global';
import { HTTPResponseCodes } from '../../constants/network';
import { UserStore } from '../../store';
import AuthService from '../auth';
import { BifrostApiErrorInterceptorList } from './constants';
import {
  APIErrorInterface,
  AxiosClientConstructorInterface,
  BaseHTTPClientConstructorInterface,
  BifrostAPIErrorInterface,
  ErrorInterceptorMiddlewareInterface,
  RequestInterceptorMiddlewareInterface,
  ResponseInterceptorMiddlewareInterface,
} from './types';

abstract class BaseHTTPClient {
  protected readonly instance: AxiosInstance;

  constructor(params: BaseHTTPClientConstructorInterface) {
    this.instance = axios.create(params);
  }

  protected abstract requestInterceptorMiddleware: RequestInterceptorMiddlewareInterface;
  protected abstract responseInterceptorMiddleware: ResponseInterceptorMiddlewareInterface;
  protected abstract errorInterceptorMiddleware: ErrorInterceptorMiddlewareInterface;
}

class AxiosClient extends BaseHTTPClient {
  constructor(params: AxiosClientConstructorInterface) {
    const { HTTPClientParams, isProtected } = params;
    super(HTTPClientParams);

    if (isProtected) {
      this.instance.interceptors.request.use(this.requestInterceptorMiddleware);
    }
    this.instance.interceptors.response.use(
      this.responseInterceptorMiddleware,
      this.errorInterceptorMiddleware
    );
  }

  protected requestInterceptorMiddleware: RequestInterceptorMiddlewareInterface =
    (config) => {
      config.headers['Authorization'] = AuthService.getAuthToken();
      config.headers['Content-Type'] =
        config.headers['Content-Type'] || 'application/json';
      config.headers['accept'] = config.headers['accept'] || 'application/json';
      config.headers['X-Entity-Code'] =
        UserStore.getState().entityCode || undefined;
      return config;
    };

  protected responseInterceptorMiddleware: ResponseInterceptorMiddlewareInterface =
    (response) => {
      if (Object.values(HTTPResponseCodes.SUCCESS).includes(response.status)) {
        return { ...response, data: response.data ?? {} };
      }
      return response;
    };

  protected errorInterceptorMiddleware: ErrorInterceptorMiddlewareInterface = (
    error
  ) => {
    const errorData = error?.response?.data;
    const isBifrostError = BifrostApiErrorInterceptorList.includes(
      error?.config?.url
    );

    const description =
      typeof errorData === 'string' && errorData.length > 0
        ? errorData
        : isBifrostError
          ? ((errorData as BifrostAPIErrorInterface)?.message ??
            (errorData as BifrostAPIErrorInterface)?.error?.message ??
            DeclarationConstants.UNKNOWN_ERROR_MESSAGE)
          : ((errorData as APIErrorInterface)?.errors?.[0]?.error?.split(
              '\n'
            )[0] ?? DeclarationConstants.UNKNOWN_ERROR_MESSAGE);

    const ErrorEvent = new CustomEvent(EventConstants.SHOW_TOAST, {
      detail: {
        message: DeclarationConstants.UNKNOWN_ERROR,
        description,
        severity: 'error',
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      },
    });

    document.dispatchEvent(ErrorEvent);

    if (
      error?.response?.status === HTTPResponseCodes.CLIENT_ERROR.UNAUTHORIZED
    ) {
      AuthService.expireToken();
    }

    return Promise.reject(error);
  };
}

export default AxiosClient;
